import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TableColumn, SelectionType } from '@swimlane/ngx-datatable';
import { CustomProductMappingService } from 'src/app/shared/api/services';
import { ClientBillingCustomProductMapping, RuleMapping } from 'src/app/shared/api/models';
import { Uuid } from 'src/app/shared/classes/uuid';
import { ErrorHandlerComponent, buttontypes } from 'src/app/shared/components/error-handler/error-handler.component';
import { FormGroup, FormControl, Validators, FormArray, FormBuilder } from '@angular/forms';
import { ObjectToFormGroup } from '../../shared/classes/helpers';

@Component({
  selector: 'e2cb-custom-product',
  templateUrl: './custom-product.component.html',
  styleUrls: ['./custom-product.component.scss']
})
export class CustomProductComponent implements OnInit {
  @ViewChild(ErrorHandlerComponent, {static: true}) errorHandlerComponent: ErrorHandlerComponent;

  public Uuid: Uuid = new Uuid();
  public SelectionType = SelectionType;
  public isLoading: boolean = false;
  public aviClientId: string = null;
  public productsColumns: TableColumn[] = [
    {
      width: 30,
      sortable: false,
      canAutoResize: false,
      draggable: false,
      resizeable: false,
      cellClass: 'checkbox-cells'
    },
    { name: 'Product Name', prop: 'newProductName' }
  ];
  public customProducts: ClientBillingCustomProductMapping[] = [];
  public selectedProducts: ClientBillingCustomProductMapping[] = [];
  public sourceFields: string[] = [];
  public conditionalFields: string[] = [];
  public productTypes: string[] = [];
  public searchedProduct: ClientBillingCustomProductMapping = null;
  public currViewedFormProducts: FormGroup[] = [];

  constructor(
    private route: ActivatedRoute,
    private customProductMappingService: CustomProductMappingService,
    public formBuilder: FormBuilder,
  ) {
  }
  // #region Public Functions
  public async ngOnInit() {
    // Listen to route params (ie. cliendId) changes
    await this.route.params.subscribe(params => {
      this.loadClientProducts(params['clientId']);
      this.sourceFields.length = 0;
      this.loadAvailableSourceFields();
    });

    // Get all the possible fields for the UI's conditionals
    this.conditionalFields = await this.customProductMappingService.GetAvailablesConditionalFields().toPromise();
    // Get all the possible Product Types for the UI
    this.productTypes = await this.customProductMappingService.GetAvailableProductTypes().toPromise();
  }

 public async loadAvailableSourceFields()
 {
    let queryParms = {clientId: this.aviClientId, Authorization: null} as CustomProductMappingService.GetAvailableSourceFieldsParams;
    let results = await this.customProductMappingService.GetAvailableSourceFields(queryParms).toPromise();

    for (const [key, value] of Object.entries(results)) {
      if (value === "String") {
        this.sourceFields.push(key);
      }
    }
 }

  public async hideProductForm(viewedFormGroup: FormGroup, forceHide: boolean = false) {
    if (viewedFormGroup.touched && !forceHide) {
      let accepted = await this.errorHandlerComponent.info("There are unsaved changes, still close without saving?", {buttons: buttontypes.yesorno, title: "Confirm Close"});
      if (!accepted) { return; }
    }

    let idx = this.selectedProducts.findIndex(c => c.clientBillingCustomProductMappingId === viewedFormGroup.value.clientBillingCustomProductMappingId );
    this.selectedProducts.splice(idx, 1);
    this.selectedProducts = [... this.selectedProducts];

    this.syncDisplayedForms();
  }

  public syncDisplayedForms() {
    // if a selected product was removed, remove it from the search box
    if (this.selectedProducts.indexOf(this.searchedProduct) === -1) {
      this.searchedProduct = null;
    }

    // Add in user selected products for form fill out
    for ( let currViewedProduct of this.selectedProducts ) {
      if ( !this.currViewedFormProducts.find( fg => fg.value.clientBillingCustomProductMappingId === currViewedProduct.clientBillingCustomProductMappingId )) {
        this.currViewedFormProducts.push( ObjectToFormGroup(currViewedProduct, this.formBuilder) );
      }
    }

    // Remove product forms that arent selected (aka. user deselected or removed it)
    this.currViewedFormProducts.forEach((formGroup, idx) => {
      if ( !this.selectedProducts.find( sp => sp.clientBillingCustomProductMappingId === formGroup.value.clientBillingCustomProductMappingId )) {
        this.currViewedFormProducts.splice(idx, 1);
      }
    });
  }
  // #endregion

  // #region Public Template Bound Functions
  public onAddProduct() {
    let newCustomProduct: ClientBillingCustomProductMapping = {
      clientBillingCustomProductMappingId: Uuid.createV4(),
      ruleMappings: [],
      newProductName: "",
      productType: "Account",
      aviClientId: this.aviClientId
    };
    this.currViewedFormProducts.push(ObjectToFormGroup(newCustomProduct, this.formBuilder));
    this.selectedProducts.push(newCustomProduct);
  }

  public onAddFormConditional(formRuleMapping: FormArray) {
    let newRuleMapping: RuleMapping = {
      condition: null,
      sourceFieldName: "",
      valueToMatch: ""
    };

    formRuleMapping.push(ObjectToFormGroup(newRuleMapping, this.formBuilder, true));
  }

  public async onSaveFormGroup(viewedFormGroup: FormGroup) {
    let formToDTO = viewedFormGroup.value as ClientBillingCustomProductMapping;


    try {
      this.isLoading = true;
      if ( "id" in formToDTO ) {
        await this.customProductMappingService.UpdateCustomProduct({value: formToDTO}).toPromise();
      } else {
        let isUniqueName = ( undefined === this.customProducts.find( cp => cp.newProductName === formToDTO.newProductName) );
        if ( !isUniqueName ) {
          throw { error: "Custom products must have unique names per client." };
        }

        await this.customProductMappingService.CreateCustomProduct({value: formToDTO}).toPromise();
      }
      this.isLoading = false;
    } catch (ex) {
      this.errorHandlerComponent.error(ex.error, {fullmessage: ex});
      this.isLoading = false;
      return;
    }

    // Refresh list and replace opened product with newly loaded one
    await this.loadClientProducts();
    let newDTO = this.customProducts.find(c => c.clientBillingCustomProductMappingId === formToDTO.clientBillingCustomProductMappingId);
    let idx = this.currViewedFormProducts.findIndex(c => c.value.clientBillingCustomProductMappingId === formToDTO.clientBillingCustomProductMappingId);
    this.currViewedFormProducts[idx] = ObjectToFormGroup(newDTO, this.formBuilder);
  }

  public async onDeleteFormGroup(viewedFormGroup: FormGroup) {
    let accepted = await this.errorHandlerComponent.info("Are you sure you want to delete this custom product?", {buttons: buttontypes.yesorno, title: "Confirm Deletion"});
    if (!accepted) { return; }

    try {
      this.isLoading = true;
      await this.customProductMappingService.Delete({value: viewedFormGroup.value as ClientBillingCustomProductMapping}).toPromise();
      await this.loadClientProducts();

      this.hideProductForm(viewedFormGroup, true);
      this.isLoading = false;
    } catch (ex) {
      this.errorHandlerComponent.error(ex.error, {fullmessage: ex});
      this.isLoading = false;
    }
  }

  public onRemoveConditional(productForm: FormGroup, ruleMapForm: FormControl) {
    let ruleMappingsForm = productForm.get('ruleMappings') as FormArray;
    let idx = ruleMappingsForm.controls.findIndex(c => c === ruleMapForm);
    ruleMappingsForm.removeAt(idx);
    productForm.markAllAsTouched();
  }

  public onProductFound(selected) {
    if (!selected || this.selectedProducts.indexOf(selected) > -1) {
      return;
    }
    this.selectedProducts.push(selected);
    this.selectedProducts = [... this.selectedProducts];

    this.syncDisplayedForms();
  }
  // #endregion

  // #region Private Functions
  private async loadClientProducts(id?: string) {
    try {
      if (id) {
        this.aviClientId = id;
        this.customProducts = [];
        this.currViewedFormProducts = [];
        this.selectedProducts = [];
        this.searchedProduct = null;
      }

      this.isLoading = true;
      this.customProducts = await this.customProductMappingService.GetAllForClient({aviClientId: this.aviClientId}).toPromise();

      // Refresh any of the selected products that may exist
      this.selectedProducts.forEach( (sp, idx) => {
        let dto = this.customProducts.find( cpr => cpr.clientBillingCustomProductMappingId === sp.clientBillingCustomProductMappingId);
        if (dto) { this.selectedProducts[idx] = dto; }
      });

      this.isLoading = false;
    } catch (ex) {
      this.errorHandlerComponent.error(ex.error, {fullmessage: ex});
      this.isLoading = false;
    }
  }
  // #endregion
}
